import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Save, Plus, Trash2, Edit2, AlertCircle, CheckCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChatbotSettings = () => {
  const { getAuthHeader } = useAuth();
  const { toast } = useToast();
  const [qaList, setQaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [config, setConfig] = useState({
    manual_chat_enabled: false
  });
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    order: 0,
    enabled: true
  });

  useEffect(() => {
    fetchConfig();
    fetchQA();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/chatbot/admin/config`, {
        headers: getAuthHeader()
      });
      setConfig(response.data || { manual_chat_enabled: false });
    } catch (error) {
      console.error('Failed to fetch config:', error);
    }
  };

  const updateConfig = async (field, value) => {
    try {
      await axios.put(`${API}/chatbot/admin/config`, {
        [field]: value
      }, {
        headers: getAuthHeader()
      });
      
      setConfig(prev => ({ ...prev, [field]: value }));
      
      toast({
        title: "Success",
        description: "Chatbot configuration updated",
        variant: "success"
      });
    } catch (error) {
      console.error('Failed to update config:', error);
      toast({
        title: "Error",
        description: "Failed to update configuration",
        variant: "destructive"
      });
    }
  };

  const fetchQA = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/chatbot/admin/chatbot/qa`, {
        headers: getAuthHeader()
      });
      setQaList(response.data || []);
    } catch (error) {
      console.error('Failed to fetch Q&A:', error);
      toast({
        title: "Error",
        description: "Failed to load Q&A settings",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.question.trim() || !formData.answer.trim()) {
      toast({
        title: "Error",
        description: "Question and answer are required",
        variant: "destructive"
      });
      return;
    }

    try {
      if (editingId) {
        await axios.put(
          `${API}/chatbot/admin/chatbot/qa/${editingId}`,
          formData,
          { headers: getAuthHeader() }
        );
        toast({ title: "Success", description: "Q&A updated successfully" });
      } else {
        await axios.post(
          `${API}/chatbot/admin/chatbot/qa`,
          formData,
          { headers: getAuthHeader() }
        );
        toast({ title: "Success", description: "Q&A created successfully" });
      }
      
      setFormData({ question: '', answer: '', order: 0, enabled: true });
      setEditingId(null);
      fetchQA();
    } catch (error) {
      console.error('Failed to save Q&A:', error);
      toast({
        title: "Error",
        description: "Failed to save Q&A",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (qa) => {
    setFormData({
      question: qa.question,
      answer: qa.answer,
      order: qa.order,
      enabled: qa.enabled
    });
    setEditingId(qa.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    const confirmed = await window.confirm('Are you sure you want to delete this Q&A?');
    if (!confirmed) return;

    try {
      await axios.delete(
        `${API}/chatbot/admin/chatbot/qa/${id}`,
        { headers: getAuthHeader() }
      );
      toast({ title: "Success", description: "Q&A deleted successfully" });
      fetchQA();
    } catch (error) {
      console.error('Failed to delete Q&A:', error);
      toast({
        title: "Error",
        description: "Failed to delete Q&A",
        variant: "destructive"
      });
    }
  };

  const cancelEdit = () => {
    setFormData({ question: '', answer: '', order: 0, enabled: true });
    setEditingId(null);
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-state">Loading...</div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1 className="heading-1">Chatbot Settings</h1>
          <p className="body-large">Manage chatbot configuration and Q&A</p>
        </div>
      </div>

      {/* Configuration Card */}
      <div className="settings-card" style={{ marginBottom: '32px' }}>
        <h2 className="heading-2">Chatbot Configuration</h2>
        
        <div style={{ marginTop: '20px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            padding: '16px',
            background: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <div>
              <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600' }}>
                Manual Chat (Live Agent)
              </h3>
              <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                {config.manual_chat_enabled 
                  ? 'Users can request to connect with a live agent. Fallback to Q&A if no agents available.'
                  : 'Users will only interact with predefined Q&A. Live agent option is hidden.'}
              </p>
            </div>
            
            <label className="toggle-switch" style={{ marginLeft: '20px' }}>
              <input
                type="checkbox"
                checked={config.manual_chat_enabled}
                onChange={(e) => updateConfig('manual_chat_enabled', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
      </div>

      {/* Q&A Form */}
      <div className="settings-card" style={{ marginBottom: '32px' }}>
        <h2 className="heading-2">{editingId ? 'Edit Q&A' : 'Add New Q&A'}</h2>
        <form onSubmit={handleSubmit} className="settings-form">
          <div className="form-group">
            <label className="form-label">Question</label>
            <input
              type="text"
              value={formData.question}
              onChange={(e) => setFormData({...formData, question: e.target.value})}
              className="form-input"
              placeholder="e.g., What documents do I need for a tourist visa?"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Answer</label>
            <textarea
              value={formData.answer}
              onChange={(e) => setFormData({...formData, answer: e.target.value})}
              className="form-textarea"
              rows={5}
              placeholder="Enter the answer that will be shown when this question is selected..."
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Display Order</label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({...formData, order: parseInt(e.target.value)})}
                className="form-input"
                min="0"
              />
            </div>
            <div className="form-group">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={formData.enabled}
                  onChange={(e) => setFormData({...formData, enabled: e.target.checked})}
                  style={{ width: 'auto' }}
                />
                Enabled
              </label>
            </div>
          </div>

          <div className="form-actions" style={{ display: 'flex', gap: '12px' }}>
            <button type="submit" className="btn-primary">
              <Save size={20} />
              <span>{editingId ? 'Update Q&A' : 'Add Q&A'}</span>
            </button>
            {editingId && (
              <button type="button" onClick={cancelEdit} className="btn-secondary">
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Q&A List */}
      <div className="settings-card">
        <h2 className="heading-2">Existing Q&A ({qaList.length})</h2>
        
        {qaList.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
            <AlertCircle size={48} style={{ opacity: 0.5, marginBottom: '16px' }} />
            <p className="body-medium">No Q&A pairs yet. Add your first one above!</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {qaList.map((qa) => (
              <div
                key={qa.id}
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '16px',
                  background: qa.enabled ? 'white' : '#f9fafb'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <h3 className="heading-3" style={{ margin: 0 }}>{qa.question}</h3>
                      {!qa.enabled && (
                        <span style={{ 
                          fontSize: '11px', 
                          color: '#ef4444', 
                          background: '#fee2e2', 
                          padding: '2px 8px', 
                          borderRadius: '4px' 
                        }}>
                          Disabled
                        </span>
                      )}
                    </div>
                    <p className="body-medium" style={{ color: '#6b7280', margin: 0 }}>
                      {qa.answer}
                    </p>
                    <p className="body-small" style={{ color: '#9ca3af', marginTop: '8px' }}>
                      Order: {qa.order}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleEdit(qa)}
                      className="btn-secondary"
                      style={{ padding: '8px 12px' }}
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(qa.id)}
                      className="btn-secondary"
                      style={{ padding: '8px 12px', color: '#ef4444' }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatbotSettings;
